<template>
  <div v-if="document" class="flex flex-col h-full">
    <BaseDetailsPreviewEditHeader
      :is-horizontal-layout="isHorizontalLayout"
      :title="
        modifiedDocument?.documentHeader.documentNumber.toString()
          ? modifiedDocument?.documentHeader.documentNumber.toString()
          : '-'
      "
      link-name="documents-management"
      :mode="mode"
      :is-edit-active="document.isFromManager || false"
      @edit-mode="mode = 'edit'"
      @horizontal-layout="(value: boolean) => (isHorizontalLayout = value)" />

    <AppResizableContainer :layout="isHorizontalLayout ? 'vertical' : 'horizontal'" :minSize="300">
      <template #headerContent>
        <!-- Horizontal Layout -->
        <template v-if="isHorizontalLayout">
          <PriceCollectionDocumentCreateEdit
            v-if="modifiedDocument"
            v-model="modifiedDocument"
            :mode="mode"
            :isHorizontalLayout="isHorizontalLayout"
            :key="document?.id" />
        </template>

        <!-- Vertical Layout -->
        <template v-else>
          <PerfectScrollbar class="w-full h-full py-4 space-y-2" ref="scrollbarId">
            <PriceCollectionDocumentCreateEdit
              v-if="modifiedDocument"
              v-model="modifiedDocument"
              :mode="mode"
              :isHorizontalLayout="isHorizontalLayout"
              :key="document?.id" />
          </PerfectScrollbar>
        </template>
      </template>

      <template #detailsContent>
        <BaseTable
          v-if="documentDetails"
          :headers="tableHeaders"
          :list="documentDetails"
          :total-records="documentDetailsTotalCount"
          :is-add-new-hidden="true"
          hide-grouping
          table-id="priceCollectionDocumentDetails"
          @refresh-data="refreshData">
        </BaseTable>
      </template>
    </AppResizableContainer>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useLookupsStore } from '@/stores/lookups';

import AppResizableContainer from '@/components/common/AppResizableContainer.vue';
import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import type {
  PriceCollectionDocument,
  PriceCollectionDocumentDetails,
} from '@/types/PriceCollectionDocuments';
import PriceCollectionDocumentCreateEdit from '@/components/documents/PriceCollectionDocumentCreateEdit.vue';
import {
  getPriceCollectionDocumentById,
  getPriceCollectionDocumentDetailsWithConditions,
} from '@/services/api/priceCollection';
import type { TableFilterData } from '@/types/Base';
import { filterData, sortData } from '@/utils/table';
import { deepCopy } from '@/utils/data';
import { useI18n } from 'vue-i18n';

const { getUsersLookup } = useLookupsStore();
const route = useRoute();

const document = ref<PriceCollectionDocument>();
const modifiedDocument = ref<PriceCollectionDocument>();
const isHorizontalLayout = ref(true);
const mode = ref<'view' | 'edit'>('view');

const documentDetails = ref<PriceCollectionDocumentDetails[]>();
const documentDetailsTotalCount = ref<number>();

const { t: $t } = useI18n({ useScope: 'global' });
const tableHeaders = [
  { value: 'order', text: $t('baseObject.sequence'), type: 'text', active: true },
  { value: 'material.name', text: $t('material.name'), type: 'text', active: true },
  { value: 'competitor.profession', text: $t('material.competitor'), type: 'text', active: true },
  { value: 'alterCode.name', text: $t('material.alterCode'), type: 'text', active: true },
  { value: 'alterDescription', text: $t('groups.description'), type: 'text', active: true },
  { value: 'currentPrice', text: $t('salesDocuments.currentPrice'), type: 'text', active: true },
  { value: 'shelf', text: $t('salesDocuments.shelf'), type: 'text', active: true },
  { value: 'faces', text: $t('salesDocuments.faces'), type: 'text', active: true },
  { value: 'lastPrice', text: $t('pricing-catalogs.previousPrice'), type: 'text', active: true },
  {
    value: 'lastCollectionDate',
    text: $t('salesDocuments.lastCollectionDate'),
    type: 'text',
    active: true,
  },
  { value: 'havePromo', text: $t('salesDocuments.havePromo'), type: 'check', active: true },
  { value: 'notExist', text: $t('salesDocuments.notExist'), type: 'check', active: true },
  { value: 'remarks', text: $t('material.remarks'), type: 'text', active: true },
  { value: 'forPrinting', text: $t('salesDocuments.forPrinting'), type: 'check', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
];

type QueryParams = {
  page?: number;
  itemsPerPage: number;
  query?: string;
  sortColumn?: string;
  sortAscending?: boolean;
  isActive?: boolean;
};

onMounted(async () => {
  await getUsersLookup();
  await loadData({
    itemsPerPage: 50,
    sortColumn: 'order',
    sortAscending: true,
    page: 1,
  });
});

async function loadData(params: QueryParams) {
  const responseDocument = await getPriceCollectionDocumentById(route.params.id as string);
  if (responseDocument) {
    document.value = responseDocument;
    mode.value = 'view';
  }
  if (document.value) {
    modifiedDocument.value = deepCopy<PriceCollectionDocument>(document.value);
  }

  const responseDocumentDetails = await getPriceCollectionDocumentDetailsWithConditions({
    ...params,
    documentId: modifiedDocument.value?.id || '',
  });
  if (responseDocumentDetails) {
    documentDetails.value = responseDocumentDetails.data;
    mode.value = 'view';
    documentDetailsTotalCount.value = responseDocumentDetails.count;
  }
}

async function refreshData(data: TableFilterData) {
  const sortColumn = data.column !== undefined ? tableHeaders[data.column]?.value : 'id';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params: QueryParams = {
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
    isActive: data.isActive,
  };

  await loadData(params);

  if (data.query && documentDetails.value)
    documentDetails.value = filterData(documentDetails.value, data.query, [
      'material.name',
      'competitor.profession',
      'alterCode.name',
    ]);
  if (data.column !== undefined && data.order && documentDetails.value)
    documentDetails.value = sortData(documentDetails.value, data.column, data.order, tableHeaders);
}
</script>
